// Synthesized call tones (no audio assets needed).
// - ringback: what the caller hears while the other side rings (440+480Hz, 2s on / 4s off)
// - ringtone: incoming call ring (two short dual-tone bursts, repeating)
// - end: short descending tone when a call ends
//
// In the apps the incoming ringtone is played natively so it follows the
// phone's ringer / silent mode (see notificationSound.js). Ringback and the end
// tone are part of a call the user is already in, so they always play.
//
// Browsers keep AudioContext suspended until a user gesture. startCall awaits
// getUserMedia first, which often drops that gesture — so ringback is scheduled
// while still suspended, stays silent for the whole call, then plays when hang-up
// finally resumes the context. Unlock on the Call tap, and never schedule tones
// unless the context is actually running.

import {
  playNativeNotificationSound, stopNativeNotificationSound, usesNativeNotificationSound,
} from './notificationSound';

let ctx = null;
let loopTimer = null;
let activeNodes = [];
let toneGen = 0;
let ringtoneEl = null;
let ringtoneWanted = false;

const getCtx = () => {
  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass) return null;
  if (!ctx || ctx.state === 'closed') ctx = new AudioContextClass();
  return ctx;
};

// Call this from the Call button click *before* any await so the context is
// allowed to run by the time ringback starts.
export const unlockCallAudio = () => {
  const c = getCtx();
  if (!c) return;
  if (c.state === 'suspended') {
    c.resume().catch((err) => console.warn('AudioContext resume failed:', err));
  }
  try {
    const gain = c.createGain();
    gain.gain.value = 0;
    const osc = c.createOscillator();
    osc.connect(gain);
    gain.connect(c.destination);
    osc.start();
    osc.stop(c.currentTime + 0.02);
  } catch {
    // Context may still be suspended; later playOnce() will resume if it can.
  }
};

const whenRunning = (c, gen, fn) => {
  if (!c || gen !== toneGen) return;
  const run = () => {
    if (gen !== toneGen || c.state !== 'running') return;
    fn();
  };
  if (c.state === 'running') {
    run();
    return;
  }
  c.resume().then(run).catch((err) => console.warn('AudioContext resume failed:', err));
};

const beep = (freqs, start, duration, level = 0.08) => {
  const c = getCtx();
  if (!c || c.state !== 'running') return;
  const gain = c.createGain();
  gain.gain.setValueAtTime(0, start);
  gain.gain.linearRampToValueAtTime(level, start + 0.02);
  gain.gain.setValueAtTime(level, start + duration - 0.03);
  gain.gain.linearRampToValueAtTime(0, start + duration);
  gain.connect(c.destination);
  activeNodes.push(gain);
  freqs.forEach((f) => {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = f;
    osc.connect(gain);
    osc.start(start);
    osc.stop(start + duration);
    activeNodes.push(osc);
  });
};

export const stopCallTone = () => {
  toneGen += 1;
  if (ringtoneWanted && usesNativeNotificationSound()) stopNativeNotificationSound();
  ringtoneWanted = false;
  if (ringtoneEl) {
    ringtoneEl.pause();
    ringtoneEl.currentTime = 0;
  }
  clearInterval(loopTimer);
  loopTimer = null;
  activeNodes.forEach((n) => {
    try { n.stop(); } catch { /* already stopped or a GainNode */ }
    try { n.disconnect(); } catch { /* already disconnected */ }
  });
  activeNodes = [];
};

const loop = (playOnce, periodMs) => {
  stopCallTone();
  const gen = toneGen;
  const tick = () => {
    if (gen !== toneGen) return;
    const c = getCtx();
    if (!c) return;
    whenRunning(c, gen, () => {
      activeNodes = [];
      playOnce();
    });
  };
  try {
    tick();
    loopTimer = setInterval(tick, periodMs);
  } catch (e) {
    console.warn('Call tone playback failed', e);
  }
};

export const playRingback = () =>
  loop(() => {
    const c = getCtx();
    if (c) beep([440, 480], c.currentTime, 2, 0.05);
  }, 6000);

const playSynthRingtone = () =>
  loop(() => {
    const c = getCtx();
    if (!c) return;
    beep([1300, 1700], c.currentTime, 0.4);
    beep([1300, 1700], c.currentTime + 0.55, 0.4);
  }, 3000);

export const playRingtone = () => {
  stopCallTone();
  ringtoneWanted = true;
  const gen = toneGen;
  if (usesNativeNotificationSound()) {
    // Rings, vibrates or stays quiet according to the ringer (no web fallback:
    // silence here is intentional)
    playNativeNotificationSound('ringtone');
    return;
  }
  try {
    ringtoneEl = ringtoneEl || new Audio(`${process.env.PUBLIC_URL || ''}/ringtone.wav`);
    ringtoneEl.loop = true;
    ringtoneEl.currentTime = 0;
    ringtoneEl.play().then(() => {
      if (!ringtoneWanted || gen !== toneGen) {
        ringtoneEl.pause();
        ringtoneEl.currentTime = 0;
      }
    }).catch((err) => {
      console.warn('Ringtone playback failed, using synthesized ring', err);
      if (ringtoneWanted && gen === toneGen) playSynthRingtone();
    });
  } catch {
    if (ringtoneWanted && gen === toneGen) playSynthRingtone();
  }
};

export const playEndTone = () => {
  stopCallTone();
  const gen = toneGen;
  const c = getCtx();
  if (!c) return;
  whenRunning(c, gen, () => {
    beep([620], c.currentTime, 0.15, 0.06);
    beep([480], c.currentTime + 0.18, 0.2, 0.06);
  });
};
